<template>
  <HrPageLayout
    title="My Attendance Records"
    subtitle="View and manage your attendance history"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Attendance Records' }
    ]"
  >
    <template #header-actions>
    </template>

    <div class="attendance-records-content">
      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-filters-row hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Start Date</label>
              <input v-model="filters.startDate" type="date" class="hr-input">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">End Date</label>
              <input v-model="filters.endDate" type="date" class="hr-input" :max="today">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Filter by Status</label>
              <select v-model="filters.status" class="hr-select">
                <option value="">All Status</option>
                <option value="Present">Present</option>
                <option value="Late">Late</option>
                <option value="Partial">Partial</option>
                <option value="Absent">Absent</option>
                <option value="Overtime">Overtime</option>
              </select>
            </div>
            <div class="hr-filter-group hr-align-end">
              <button @click="loadAttendanceRecords" class="hr-btn hr-btn-primary">
                <i class="bx bx-filter-alt"></i> Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p>Loading attendance records...</p>
      </div>

      <div v-else-if="attendanceData">
        <!-- Statistics Cards -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Attendance Rate</p>
            <h2 class="hr-metric-value">{{ attendanceData.statistics.attendanceRate }}%</h2>
            <small class="hr-text-muted">
              {{ attendanceData.statistics.presentDays + attendanceData.statistics.lateDays }}/{{ attendanceData.statistics.workingDays }} days
            </small>
            <div class="hr-progress-bar">
              <div class="hr-progress-fill hr-badge-success"
                   :style="{ width: attendanceData.statistics.attendanceRate + '%' }"></div>
            </div>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Total Hours</p>
            <h2 class="hr-metric-value">{{ attendanceData.statistics.totalHours }}h</h2>
            <small class="hr-text-muted">Avg: {{ attendanceData.statistics.avgHours }}h/day</small>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time"></i></div>
            <p class="hr-metric-label">Late Arrivals</p>
            <h2 class="hr-metric-value">{{ attendanceData.statistics.lateDays }}</h2>
            <small class="hr-text-muted">{{ attendanceData.statistics.totalLateMinutes }} min total</small>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-trending-up"></i></div>
            <p class="hr-metric-label">Overtime</p>
            <h2 class="hr-metric-value">{{ attendanceData.statistics.totalOvertime }}h</h2>
            <small class="hr-text-muted">Extra hours worked</small>
          </div>
        </div>

        <!-- Average Times & Monthly Breakdown -->
        <div class="hr-content-grid hr-mb-4">
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-time"></i> Average Times</h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-info-row">
                <span class="hr-text-muted">Check In</span>
                <strong class="hr-text-success">{{ attendanceData.statistics.avgCheckIn || 'N/A' }}</strong>
              </div>
              <div class="hr-info-row">
                <span class="hr-text-muted">Check Out</span>
                <strong class="hr-text-danger">{{ attendanceData.statistics.avgCheckOut || 'N/A' }}</strong>
              </div>
            </div>
          </div>
          <div class="hr-chart-card hr-span-2">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-line-chart"></i> Last 6 Months Overview</h5>
            </div>
            <div class="hr-chart-body">
              <div v-if="attendanceData.monthlyBreakdown.length === 0" class="hr-text-muted hr-text-center hr-py-3">
                No data available
              </div>
              <div v-else class="hr-table-wrapper">
                <table class="hr-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th class="hr-text-center">Days</th>
                      <th class="hr-text-center">Hours</th>
                      <th class="hr-text-center">Late</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="month in attendanceData.monthlyBreakdown" :key="month.month">
                      <td>{{ formatMonth(month.month) }}</td>
                      <td class="hr-text-center">{{ month.days }}</td>
                      <td class="hr-text-center">{{ parseFloat(month.hours).toFixed(1) }}h</td>
                      <td class="hr-text-center">
                        <span v-if="month.late_count > 0" class="hr-badge hr-badge-warning">{{ month.late_count }}</span>
                        <span v-else class="hr-text-muted">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendance Records Table -->
        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <h5 class="hr-chart-title"><i class="bx bx-table"></i> Attendance Records</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="attendanceData.attendanceRecords.length === 0" class="hr-empty-state">
              <div class="hr-empty-icon"><i class="bx bx-calendar"></i></div>
              <h5>No attendance records found</h5>
              <p class="hr-text-muted">Try adjusting your filters</p>
            </div>

            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th class="hr-text-center">Hours</th>
                    <th class="hr-text-center">Break</th>
                    <th class="hr-text-center">Overtime</th>
                    <th class="hr-text-center">Late</th>
                    <th>Status</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in attendanceData.attendanceRecords" :key="record.id" @click="showRecordDetails(record)">
                    <td><strong>{{ formatDate(record.date) }}</strong></td>
                    <td>{{ getDayOfWeek(record.date) }}</td>
                    <td>
                      <span v-if="record.first_in" class="hr-text-success">
                        <i class="bx bx-log-in"></i> {{ record.first_in }}
                      </span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="record.last_out" class="hr-text-danger">
                        <i class="bx bx-log-out"></i> {{ record.last_out }}
                      </span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td class="hr-text-center">
                      <span class="hr-badge hr-badge-primary">{{ record.total_hours || '0' }}h</span>
                    </td>
                    <td class="hr-text-center">{{ record.break_hours || '0' }}h</td>
                    <td class="hr-text-center">
                      <span v-if="record.overtime_hours > 0" class="hr-badge hr-badge-info">{{ record.overtime_hours }}h</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td class="hr-text-center">
                      <span v-if="record.late_minutes > 0" class="hr-badge hr-badge-warning">{{ record.late_minutes }}m</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td>
                      <span :class="'hr-badge ' + getStatusBadgeClass(record.status)">{{ record.status }}</span>
                    </td>
                    <td><small class="hr-text-muted">{{ record.location || 'N/A' }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="attendanceData.pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-3">
              <div class="hr-pagination-info">
                Showing {{ ((attendanceData.pagination.currentPage - 1) * attendanceData.pagination.limit) + 1 }}
                to {{ Math.min(attendanceData.pagination.currentPage * attendanceData.pagination.limit, attendanceData.pagination.totalRecords) }}
                of {{ attendanceData.pagination.totalRecords }} records
              </div>
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="attendanceData.pagination.currentPage === 1"
                        @click="changePage(attendanceData.pagination.currentPage - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <template v-for="page in visiblePages" :key="page">
                  <button v-if="page !== '...'" type="button" class="hr-page-btn"
                          :class="{ 'hr-page-active': page === attendanceData.pagination.currentPage }"
                          @click="changePage(page)">{{ page }}</button>
                  <span v-else class="hr-page-ellipsis">...</span>
                </template>
                <button type="button" class="hr-page-btn"
                        :disabled="attendanceData.pagination.currentPage === attendanceData.pagination.totalPages"
                        @click="changePage(attendanceData.pagination.currentPage + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Record Details Modal -->
    <ModalWrapper ref="recordDetailsModal" title="Attendance Details" size="large" :close-on-overlay="true">
      <template #header>
        <span v-if="selectedRecord">
          <i class="bx bx-calendar"></i> {{ formatDateLong(selectedRecord.date) }}
        </span>
      </template>
      <template #body v-if="selectedRecord">
        <div :class="'hr-alert hr-alert-' + getStatusAlertClass(selectedRecord.status)" role="alert">
          <h6><i :class="getStatusIcon(selectedRecord.status)"></i> Status: {{ selectedRecord.status }}</h6>
        </div>
        <div class="hr-detail-grid hr-mb-4">
          <h6 class="hr-section-title"><i class="bx bx-time"></i> Time Details</h6>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">First Check In</label>
            <p class="hr-mb-0"><i class="bx bx-log-in hr-text-success"></i> {{ selectedRecord.first_in || 'N/A' }}</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Last Check Out</label>
            <p class="hr-mb-0"><i class="bx bx-log-out hr-text-danger"></i> {{ selectedRecord.last_out || 'N/A' }}</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Total Hours</label>
            <p class="hr-mb-0">{{ selectedRecord.total_hours || '0' }} hours</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Break Hours</label>
            <p class="hr-mb-0">{{ selectedRecord.break_hours || '0' }} hours</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Overtime</label>
            <p class="hr-mb-0">{{ selectedRecord.overtime_hours || '0' }} hours</p>
          </div>
        </div>
        <div class="hr-detail-grid hr-mb-4">
          <h6 class="hr-section-title"><i class="bx bx-line-chart"></i> Performance Metrics</h6>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Late Minutes</label>
            <p class="hr-mb-0">
              <span v-if="selectedRecord.late_minutes > 0" class="hr-badge hr-badge-warning">{{ selectedRecord.late_minutes }} minutes</span>
              <span v-else class="hr-text-success">On Time</span>
            </p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Early Departure</label>
            <p class="hr-mb-0">
              <span v-if="selectedRecord.early_departure_minutes > 0" class="hr-badge hr-badge-warning">{{ selectedRecord.early_departure_minutes }} minutes</span>
              <span v-else class="hr-text-success">None</span>
            </p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Longest Break</label>
            <p class="hr-mb-0">{{ selectedRecord.longest_break || '0' }} minutes</p>
          </div>
        </div>
        <div class="hr-detail-grid">
          <h6 class="hr-section-title"><i class="bx bx-info-circle"></i> Activity Summary</h6>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Total Entries</label>
            <p class="hr-mb-0">{{ selectedRecord.total_entries || '0' }}</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Total Exits</label>
            <p class="hr-mb-0">{{ selectedRecord.total_exits || '0' }}</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Location</label>
            <p class="hr-mb-0">{{ selectedRecord.location || 'N/A' }}</p>
          </div>
          <div class="hr-detail-item">
            <label class="hr-text-muted small">Day of Week</label>
            <p class="hr-mb-0">{{ getDayOfWeek(selectedRecord.date) }}</p>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.recordDetailsModal?.close()">Close</button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceRecords",
  components: { HrPageLayout, ModalWrapper },

  data() {
    return {
      loading: false,
      attendanceData: null,
      filters: {
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        status: ''
      },
      today: new Date().toISOString().split('T')[0],
      selectedRecord: null
    };
  },

  computed: {
    visiblePages() {
      if (!this.attendanceData) return [];
      const pages = [];
      const total = this.attendanceData.pagination.totalPages;
      const current = this.attendanceData.pagination.currentPage;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }
      return pages;
    }
  },

  mounted() {
    this.loadAttendanceRecords();
  },

  methods: {
    async loadAttendanceRecords() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeAttendanceRecords");
        data.append("startDate", this.filters.startDate);
        data.append("endDate", this.filters.endDate);
        data.append("status", this.filters.status);
        data.append("page", this.attendanceData?.pagination?.currentPage || 1);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.attendanceData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load attendance records');
        }
      } catch (error) {
        this.$toast?.error("Failed to load attendance records: " + error.message) ||
        console.error("Failed to load attendance records:", error);
      } finally {
        this.loading = false;
      }
    },

    showRecordDetails(record) {
      this.selectedRecord = record;
      this.$nextTick(() => this.$refs.recordDetailsModal?.open());
    },

    changePage(page) {
      if (page < 1 || page > this.attendanceData.pagination.totalPages || page === this.attendanceData.pagination.currentPage || page === '...') return;
      this.attendanceData.pagination.currentPage = page;
      this.loadAttendanceRecords();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatDateLong(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    },
    formatMonth(monthStr) {
      if (!monthStr) return 'N/A';
      const [year, month] = monthStr.split('-');
      return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },
    getDayOfWeek(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', { weekday: 'short' });
    },
    getStatusBadgeClass(status) {
      const s = status?.toLowerCase();
      if (s === 'present') return 'hr-badge-success';
      if (s === 'late') return 'hr-badge-warning';
      if (s === 'partial') return 'hr-badge-secondary';
      if (s === 'absent') return 'hr-badge-danger';
      if (s === 'overtime') return 'hr-badge-info';
      return 'hr-badge-secondary';
    },
    getStatusAlertClass(status) {
      const s = status?.toLowerCase();
      if (s === 'present') return 'success';
      if (s === 'late') return 'warning';
      if (s === 'partial') return 'secondary';
      if (s === 'absent') return 'danger';
      if (s === 'overtime') return 'info';
      return 'info';
    },
    getStatusIcon(status) {
      const s = status?.toLowerCase();
      if (s === 'present') return 'bx bx-check-circle';
      if (s === 'late') return 'bx bx-time-five';
      if (s === 'partial') return 'bx bx-time';
      if (s === 'absent') return 'bx bx-x-circle';
      if (s === 'overtime') return 'bx bx-trending-up';
      return 'bx bx-info-circle';
    },
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.hr-progress-bar {
  height: 6px;
  background: rgba(0,0,0,0.1);
  border-radius: 4px;
  margin-top: 8px;
}
.hr-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.hr-info-row:last-child { border-bottom: none; }
.hr-span-2 { grid-column: span 2; }
@media (max-width: 768px) { .hr-span-2 { grid-column: span 1; } }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.hr-empty-state { text-align: center; padding: 3rem; }
.hr-empty-icon { font-size: 48px; color: var(--hr-muted, #6c757d); margin-bottom: 1rem; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(5,150,105,0.2); border-top-color: #059669; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
.hr-detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
.hr-section-title { grid-column: 1 / -1; margin-bottom: 8px; color: var(--hr-primary, #059669); }
.hr-detail-item label { display: block; font-size: 0.75rem; margin-bottom: 4px; }
.hr-align-end { align-self: end; }
.hr-table-hover tbody tr { cursor: pointer; }
.hr-table-hover tbody tr:hover { background: rgba(5,150,105,0.05); }
.hr-alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; }
.hr-alert-success { background: rgba(40,167,69,0.1); color: #155724; }
.hr-alert-warning { background: rgba(255,193,7,0.15); color: #856404; }
.hr-alert-danger { background: rgba(220,53,69,0.1); color: #721c24; }
.hr-alert-info { background: rgba(23,162,184,0.1); color: #0c5460; }
.hr-alert-secondary { background: rgba(108,117,125,0.15); color: #383d41; }
</style>
